import {
  BlockWrapper,
  defineBlock,
  smartProps,
  useEditor,
  useInspectorControl,
} from "@smartfire/js-block-engine";
import { EditableImg, EditableText } from "@smartfire/components";
import { lazy, useEffect, useRef, useState } from "react";
import apiFetch from "@wordpress/api-fetch";
import Button from "../button/block";
import style from "./block.module.scss";

const Inspector = lazy(() => import("./inspector.js"));

export const ProductInPost = ({
  link_type,
  product,
  productId,
  page_url,
  post_url,
  external_link,
  title,
  acf_custom_image,
  text,
  website_icon,
  fromWidget,
  ...props
}) => {
  useInspectorControl(Inspector);
  const { isEditing } = useEditor();

  const [displayedProduct, setDisplayedProduct] = useState(product);
  const isMounted = useRef(false);

  // Used in order to avoid changing state in BO while unmonted
  // Errors happen without it
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Display product in BO
  useEffect(() => {
    if (!isEditing || !productId === -1) return;

    let productParams = {
      id_product: productId,
    };

    apiFetch({
      path:
        "/sbm/v1/get_product_by_id?" +
        new URLSearchParams(productParams).toString(),
    }).then((res) => {
      if (isMounted.current) {
        setDisplayedProduct(res);
      }
    });
  }, [isEditing, productId]);

  const renderMainImage = () => {
    const toRender = [];
    switch (link_type) {
      case "product":
        if (displayedProduct?.image?.url) {
          const alt = displayedProduct?.image?.alt;
          toRender.push(
            <img
              key="productImage"
              src={displayedProduct.image.url}
              alt={alt}
              className={style.iconProduct}
              loading="lazy"
            />
          );
        }
        return toRender;
      case "page":
      case "post":
      case "external":
        if (fromWidget) {
          toRender.push(
            <EditableImg
              key="editableCustomImg"
              smartProp="customImage"
              className={style.iconProduct}
            />
          );
        } else {
          toRender.push(
            <img
              key="productImage"
              src={acf_custom_image}
              alt={link_type}
              className={style.iconProduct}
              loading="lazy"
            />
          );
        }
        return toRender;
      default:
        return <></>;
    }
  };

  const getPIPurl = () => {
    let url = undefined;
    switch (link_type) {
      case "product":
        url = displayedProduct?.link;
        break;
      case "page":
        url = page_url;
        break;
      case "post":
        url = post_url;
        break;
      case "external":
        url = external_link;
        break;
      default:
        break;
    }
    return url;
  };

  return (
    <BlockWrapper.Div className={style.block}>
      <div className={style.product}>
        <div className={style.content}>
          {renderMainImage()}
          <div className={style.info}>
            {link_type === "product" && displayedProduct?.brand?.url && (
              <img
                src={displayedProduct.brand.url}
                alt={displayedProduct?.brand?.alt}
                className={style.iconBrand}
                loading="lazy"
              />
            )}
            <EditableText
              smartProp="title"
              className={style.title}
              placeholder={"Insert a title..."}
            />
          </div>
        </div>
        <div className={style.footer}>
          {/* Si on a une website_icon cela veut dire que l'on est sur un
            product in post propre à l'article, sinon l'éditable image est 
            utilisée dans l'éditeur du widget */}
          {website_icon ? (
            <img
              src={website_icon}
              alt="Website icon"
              className={style.iconWebsite}
              loading="lazy"
            />
          ) : (
            <EditableImg smartProp="image" className={style.iconWebsite} />
          )}
          <Button
            text={text}
            color={style.darkGreen}
            colorHover={style.darkerGreen}
            link={!isEditing ? getPIPurl() : undefined}
            className={style.btn}
          />
        </div>
      </div>
    </BlockWrapper.Div>
  );
};

export default defineBlock({
  Block: ProductInPost,
  setup: {
    title: "Product in Post",
  },
  smartProps: {
    title: smartProps.string.withDefault(""),
    acf_custom_image: smartProps.string.withDefault(""),
    text: smartProps.string.withDefault(""),
    productId: smartProps.number.withDefault(-1),
    brandId: smartProps.number.withDefault(-1),
    image: smartProps.image,
    link_type: smartProps.string.withDefault("product"),
    page_url: smartProps.string.withDefault(""),
    post_url: smartProps.string.withDefault(""),
    external_link: smartProps.string.withDefault(""),
    customImage: smartProps.image,
    fromWidget: smartProps.bool.withDefault(true),
  },
});
